import React from 'react';

export type EmptyStateIllustration =
  | 'inbox'
  | 'gallery'
  | 'document'
  | 'people'
  | 'search'
  | 'tag';

interface EmptyStateProps {
  illustration?: EmptyStateIllustration;
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  /** Compact variant — smaller paddings/illustration. */
  compact?: boolean;
}

/**
 * Friendly empty state with inline SVG illustration, title and optional CTA.
 */
export default function EmptyState({
  illustration = 'inbox',
  title,
  description,
  action,
  compact = false,
}: EmptyStateProps) {
  return (
    <div className={`empty-state${compact ? ' empty-state--compact' : ''}`}>
      <div className="empty-state__illustration" aria-hidden="true">
        {renderIllustration(illustration)}
      </div>
      <h3 className="empty-state__title">{title}</h3>
      {description ? (
        <p className="empty-state__description">{description}</p>
      ) : null}
      {action ? <div className="empty-state__action">{action}</div> : null}
    </div>
  );
}

function renderIllustration(kind: EmptyStateIllustration) {
  const stroke = 'currentColor';
  const sw = 1.5;
  switch (kind) {
    case 'inbox':
      return (
        <svg viewBox="0 0 120 120" fill="none">
          <rect
            x="16"
            y="32"
            width="88"
            height="64"
            rx="12"
            stroke={stroke}
            strokeWidth={sw}
          />
          <path
            d="M16 64h24l6 12h28l6-12h24"
            stroke={stroke}
            strokeWidth={sw}
            strokeLinejoin="round"
          />
          <path
            d="M40 24h40"
            stroke={stroke}
            strokeWidth={sw}
            strokeLinecap="round"
          />
        </svg>
      );
    case 'gallery':
      return (
        <svg viewBox="0 0 120 120" fill="none">
          <rect
            x="16"
            y="24"
            width="88"
            height="72"
            rx="10"
            stroke={stroke}
            strokeWidth={sw}
          />
          <circle cx="38" cy="50" r="6" stroke={stroke} strokeWidth={sw} />
          <path
            d="M22 86l24-24 18 18 10-10 24 24"
            stroke={stroke}
            strokeWidth={sw}
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'document':
      return (
        <svg viewBox="0 0 120 120" fill="none">
          <path
            d="M28 16h44l20 20v60a8 8 0 01-8 8H28a8 8 0 01-8-8V24a8 8 0 018-8z"
            stroke={stroke}
            strokeWidth={sw}
            strokeLinejoin="round"
          />
          <path
            d="M72 16v20h20"
            stroke={stroke}
            strokeWidth={sw}
            strokeLinejoin="round"
          />
          <path
            d="M36 60h40M36 72h40M36 84h28"
            stroke={stroke}
            strokeWidth={sw}
            strokeLinecap="round"
          />
        </svg>
      );
    case 'people':
      return (
        <svg viewBox="0 0 120 120" fill="none">
          <circle cx="44" cy="48" r="14" stroke={stroke} strokeWidth={sw} />
          <circle cx="80" cy="54" r="10" stroke={stroke} strokeWidth={sw} />
          <path
            d="M20 96c0-13 11-24 24-24s24 11 24 24"
            stroke={stroke}
            strokeWidth={sw}
            strokeLinecap="round"
          />
          <path
            d="M68 92c0-9 6-16 16-16s16 7 16 16"
            stroke={stroke}
            strokeWidth={sw}
            strokeLinecap="round"
          />
        </svg>
      );
    case 'search':
      return (
        <svg viewBox="0 0 120 120" fill="none">
          <circle cx="54" cy="54" r="28" stroke={stroke} strokeWidth={sw} />
          <path
            d="M76 76l22 22"
            stroke={stroke}
            strokeWidth={sw}
            strokeLinecap="round"
          />
          <path
            d="M44 54h20M54 44v20"
            stroke={stroke}
            strokeWidth={sw}
            strokeLinecap="round"
          />
        </svg>
      );
    case 'tag':
      return (
        <svg viewBox="0 0 120 120" fill="none">
          <path
            d="M60 16h36a8 8 0 018 8v36L60 96 20 60 56 24h0a8 8 0 014-8h0z"
            stroke={stroke}
            strokeWidth={sw}
            strokeLinejoin="round"
          />
          <circle cx="80" cy="40" r="5" stroke={stroke} strokeWidth={sw} />
        </svg>
      );
  }
}
